import { motion } from "framer-motion";

const CHAPTERS = [
  {
    n: "I",
    eyebrow: "THE IMPERIAL COURT",
    title: "A Mantle of Power",
    body: "In the courts of Ethiopian emperors, the kaba was the silhouette of sovereignty. At coronations it was received as a mantle of power; in audience halls it marked rank and favour, its gold embroidery catching the light of a thousand candles.",
    img: "/images/history/scene_00.png",
    alt: "An emperor enthroned, draped in an embroidered kaba",
  },
  {
    n: "II",
    eyebrow: "THE WARRIOR'S HONOUR",
    title: "A Medal You Could Wear",
    body: "On the battlefields of the highlands, the kaba was bestowed upon warriors for courage. Where other nations pinned medals to the chest, the Habesha draped glory over the shoulders — honour you could wear, woven in velvet and gold.",
    img: "/images/history/scene_01.png",
    alt: "Highland warriors wearing kaba capes over white shemma",
  },
  {
    n: "III",
    eyebrow: "THE STONE CHURCHES",
    title: "Wrapped in the Sacred",
    body: "In the ancient rock-hewn churches of the highlands, priests and deacons wrapped themselves in the kaba on holy days. Beneath processional crosses and brocade umbrellas, velvet and gold became part of the liturgy itself.",
    img: "/images/history/scene_02.png",
    alt: "Priests in embroidered kabas outside a rock-hewn church",
  },
  {
    n: "IV",
    eyebrow: "THE WEDDING CROWN",
    title: "Two Families, One Cloth",
    body: "At Habesha weddings, bride and groom wear the kaba like crowns of cloth. Draped in velvet and gold before the church doorway, two families are joined — and the oldest garment in the highlands blesses its newest beginning.",
    img: "/images/history/scene_03.png",
    alt: "A bride and groom in wedding kabas and crowns",
  },
];

const Reveal = ({ children, delay = 0, className = "" }) => (
  <motion.div
    initial={{ opacity: 0, y: 40 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-80px" }}
    transition={{ duration: 0.9, delay, ease: [0.22, 1, 0.36, 1] }}
    className={className}
  >
    {children}
  </motion.div>
);

const Chapter = ({ c, flip }) => (
  <article
    data-testid={`chapter-${c.eyebrow.toLowerCase().replace(/[^a-z]+/g, "-")}`}
    className={`grid items-center gap-10 lg:grid-cols-2 lg:gap-20 ${flip ? "" : ""}`}
  >
    <Reveal className={flip ? "lg:order-2" : ""}>
      <div className="group relative overflow-hidden border border-gold/20">
        <img
          src={c.img}
          alt={c.alt}
          className="aspect-[4/3] w-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.04]"
          loading="lazy"
        />
        <span className="absolute left-4 top-4 font-display text-5xl italic text-gold/85">{c.n}</span>
      </div>
    </Reveal>
    <Reveal delay={0.15} className={flip ? "lg:order-1" : ""}>
      <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-gold/80">{c.eyebrow}</p>
      <h3 className="mt-4 font-display text-2xl tracking-tight text-cream sm:text-3xl lg:text-4xl">{c.title}</h3>
      <p className="mt-5 max-w-lg text-base font-light leading-relaxed text-stone2 sm:text-lg">{c.body}</p>
    </Reveal>
  </article>
);

export default function Story() {
  return (
    <>
      <section id="origins" data-testid="origins-section" className="mx-auto max-w-7xl px-5 py-24 sm:px-10 lg:px-16 lg:py-36">
        <div className="grid items-center gap-10 lg:grid-cols-2 lg:gap-20">
          <Reveal>
            <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-gold/80">ORIGINS</p>
            <h2 className="mt-4 font-display text-2xl tracking-tight text-cream sm:text-3xl lg:text-4xl">
              The Highland Cradle
            </h2>
            <p className="mt-5 max-w-lg text-base font-light leading-relaxed text-stone2 sm:text-lg">
              High in the mountains of the Horn of Africa, among the Habesha peoples of
              Ethiopia and Eritrea, a garment became legend. The kaba — a heavy velvet
              cape embroidered with threads of gold — was worn not merely for warmth,
              but for honour. For centuries it has carried the memory of courts,
              battlefields, churches and weddings on its shoulders.
            </p>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="group relative overflow-hidden border border-gold/20">
              <img
                src="/images/history/scene_05.png"
                alt="Three generations of an Eritrean family in traditional dress"
                className="aspect-[4/3] w-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.04]"
                loading="lazy"
              />
            </div>
          </Reveal>
        </div>
      </section>

      <section id="chapters" data-testid="chapters-section" className="mx-auto max-w-7xl space-y-28 px-5 pb-24 sm:px-10 lg:px-16 lg:pb-36">
        <Reveal>
          <div className="flex items-end justify-between border-b border-gold/15 pb-6">
            <h2 className="font-display text-2xl tracking-tight text-cream sm:text-3xl lg:text-4xl">Four Chapters of Gold</h2>
            <span className="hidden font-mono text-[11px] uppercase tracking-[0.3em] text-muted2 sm:block">I — IV</span>
          </div>
        </Reveal>
        {CHAPTERS.map((c, i) => (
          <Chapter key={c.n} c={c} flip={i % 2 === 1} />
        ))}
      </section>
    </>
  );
}
